import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sopsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  BookOpen, 
  ArrowLeft,
  Loader2,
  FileText,
  Clock,
  User,
  Upload,
  Download,
  Eye,
  Plus,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const viewFileInBrowser = async (url) => {
  // Open window immediately (user gesture) to avoid popup blocker
  const newWindow = window.open('about:blank', '_blank');
  try {
    const response = await axios.get(url, { responseType: 'blob' });
    const contentType = response.headers['content-type'] || response.data.type || 'application/pdf';
    const blob = new Blob([response.data], { type: contentType });
    const viewUrl = window.URL.createObjectURL(blob);
    if (newWindow) {
      newWindow.location.href = viewUrl;
    } else {
      window.open(viewUrl, '_blank');
    }
  } catch (e) {
    if (newWindow) newWindow.close();
    toast.error('Failed to open document');
  }
};

const downloadFileAuth = async (url) => {
  try {
    const response = await axios.get(url, { responseType: 'blob' });
    const disposition = response.headers['content-disposition'];
    let filename = 'document';
    if (disposition) {
      const match = disposition.match(/filename=([^;]+)/);
      if (match) filename = match[1].replace(/"/g, '');
    }
    const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  } catch (e) {
    toast.error('Failed to download document');
  }
};

export const SOPDetailPage = () => {
  const { sopCode } = useParams();
  const navigate = useNavigate();
  const { isManagement, isAdmin } = useAuth();
  const [sop, setSop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewVersionDialog, setShowNewVersionDialog] = useState(false);
  const [newVersion, setNewVersion] = useState({ version: '', content: '' });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedVersionForUpload, setSelectedVersionForUpload] = useState(null);

  const fetchSOP = async () => {
    try {
      setLoading(true);
      const response = await sopsAPI.get(sopCode);
      setSop(response.data);
    } catch (error) {
      console.error('Failed to fetch SOP:', error);
      toast.error('Failed to load SOP');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSOP();
  }, [sopCode]);

  const handleAddVersion = async () => {
    if (!newVersion.version || !newVersion.content) {
      toast.error('Please fill in version number and content');
      return;
    }

    try {
      setSaving(true);
      await sopsAPI.addVersion(sopCode, newVersion.version, newVersion.content);
      toast.success('Version added successfully');
      setShowNewVersionDialog(false);
      setNewVersion({ version: '', content: '' });
      
      // Refresh SOP data
      const response = await sopsAPI.get(sopCode);
      setSop(response.data);
    } catch (error) {
      console.error('Failed to add version:', error);
      toast.error(error.response?.data?.detail || 'Failed to add version');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedVersionForUpload) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      await axios.post(
        `${API_URL}/api/sops/${sopCode}/upload?version=${encodeURIComponent(selectedVersionForUpload)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Document uploaded successfully');
      
      // Refresh SOP data
      const sopResponse = await sopsAPI.get(sopCode);
      setSop(sopResponse.data);
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      setSelectedVersionForUpload(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const triggerUpload = (version) => {
    setSelectedVersionForUpload(version);
    fileInputRef.current?.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!sop) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-slate-600">SOP not found</p>
        <Button onClick={() => navigate('/sops')} className="mt-4">
          Back to SOPs
        </Button>
      </div>
    );
  }

  const currentVersion = sop.versions?.find(v => v.is_current) || sop.versions?.[sop.versions.length - 1];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sop-detail-page">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleUploadDocument}
        className="hidden"
        accept=".doc,.docx,.pdf"
      />

      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/sops')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="font-mono text-lg font-bold text-slate-500">{sop.code}</span>
            {currentVersion && (
              <Badge variant="outline">v{currentVersion.version}</Badge>
            )}
            <Badge className={
              sop.category === 'fleet' ? 'bg-blue-100 text-blue-700' :
              sop.category === 'driver' ? 'bg-emerald-100 text-emerald-700' :
              'bg-purple-100 text-purple-700'
            }>
              {sop.category}
            </Badge>
          </div>
          <h1 
            className="text-2xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            {sop.title}
          </h1>
          <p className="text-slate-500 mt-1">{sop.description}</p>
        </div>
        {isManagement && (
          <Button onClick={() => setShowNewVersionDialog(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Version
          </Button>
        )}
      </div>

      {/* Current Version Content */}
      {currentVersion && (
        <Card>
          <CardHeader>
            <CardTitle 
              className="text-lg uppercase tracking-wide"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              Current Version Content
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose max-w-none">
              <p className="text-slate-700 whitespace-pre-wrap">{currentVersion.content}</p>
            </div>
            <div className="flex items-center gap-4 mt-4 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Effective: {new Date(currentVersion.effective_date).toLocaleDateString()}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attached Documents */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle 
            className="text-lg uppercase tracking-wide"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            Attached Documents
          </CardTitle>
          {isManagement && currentVersion && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => triggerUpload(currentVersion.version)}
              disabled={uploading}
              className="gap-2"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Upload Document
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {sop.attachment_urls && sop.attachment_urls.length > 0 ? (
            <div className="space-y-2">
              {sop.attachment_urls.map((url, index) => {
                const filename = url.split('/').pop();
                return (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-sm"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-slate-500" />
                      <span className="font-medium text-slate-700">{filename}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => viewFileInBrowser(`${API_URL}${url}`)}
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                      <button 
                        onClick={() => downloadFileAuth(`${API_URL}${url}`)}
                        className="text-slate-600 hover:text-slate-800 flex items-center gap-1 text-sm"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                      {isAdmin && (
                        <button 
                          onClick={async () => {
                            if (!window.confirm('Delete this document?')) return;
                            try {
                              await axios.delete(`${API_URL}/api/sops/${sopCode}/attachment?file_url=${encodeURIComponent(url)}`);
                              toast.success('Document deleted');
                              fetchSOP();
                            } catch (err) {
                              toast.error('Failed to delete document');
                            }
                          }}
                          className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm"
                          data-testid={`delete-attachment-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No documents attached yet</p>
              {isManagement && (
                <p className="text-sm mt-1">Click "Upload Document" to add a Word or PDF file</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Version History */}
      <Card>
        <CardHeader>
          <CardTitle 
            className="text-lg uppercase tracking-wide"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            Version History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sop.versions && sop.versions.length > 0 ? (
            <div className="space-y-3">
              {sop.versions.slice().reverse().map((version, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-sm border ${
                    version.is_current ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Badge variant={version.is_current ? 'default' : 'outline'}>
                      v{version.version}
                    </Badge>
                    <span className="text-slate-700">
                      {new Date(version.effective_date).toLocaleDateString()}
                    </span>
                    {version.is_current && (
                      <Badge className="bg-emerald-100 text-emerald-700">Current</Badge>
                    )}
                  </div>
                  {isManagement && !version.is_current && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => triggerUpload(version.version)}
                      disabled={uploading}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">No versions recorded</p>
          )}
        </CardContent>
      </Card>

      {/* Add Version Dialog */}
      <Dialog open={showNewVersionDialog} onOpenChange={setShowNewVersionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>ADD NEW VERSION</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Version Number</Label>
              <Input
                value={newVersion.version}
                onChange={(e) => setNewVersion({ ...newVersion, version: e.target.value })}
                placeholder="e.g., 2.0 or V2"
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={newVersion.content}
                onChange={(e) => setNewVersion({ ...newVersion, content: e.target.value })}
                placeholder="Enter the SOP content or summary..."
                rows={6}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowNewVersionDialog(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleAddVersion} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Add Version'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SOPDetailPage;
